import { Card, Table } from '@allurion/ui';
import { useIntl } from 'react-intl';

import { TrackedPageHeader } from 'src/analytics/TrackedUI';
import { Seo } from 'src/components/Seo';
import { useAppNavigate } from 'src/hooks/useAppNavigate';

import styles from './OrderForm.module.scss';

export function BalloonOrderDetailsPage() {
  const intl = useIntl();
  const { toPreviousPage } = useAppNavigate();
  const isRejected = Math.random() > 0.5;

  const mockOrder = {
    items: [
      {
        name: 'The Allurion™ Device - REF 10D-CE',
        ref: 'FS010-26',
        price: '€ 1272',
        quantity: 2,
        total: '€ 2544',
      },
      {
        name: 'The Allurion™ Filler System',
        ref: 'FD011-12',
        price: '€ 278',
        quantity: 1,
        total: '€ 278',
      },
    ],
    shipping: {
      streetAddress: '123 Main St',
      city: 'Boston',
      state: 'MA',
      postalCode: '02118',
      country: 'USA',
      phoneNumber: '+1 123 456 7890',
      email: 'test@allurion.com',
      instructions: 'Express Service',
      deliveryDate: '2023-12-01',
    },
    billing: {
      streetAddress: '123 Main St',
      city: 'Boston',
      state: 'MA',
      postalCode: '02118',
      country: 'USA',
      phoneNumber: '+1 123 456 7890',
      email: 'test@allurion.com',
      vatId: 'US123456789',
    },
    additionalInfo: {
      notes: 'Please handle with care.',
      clinicReferenceId: 'CR123456',
    },
    total: '€ 2822',
    status: 'Shipped',
    shippingCost: '€ 50',
    taxes: '€ 100',
  };

  const statusSteps = [
    { label: 'Created', completed: true },
    { label: isRejected ? 'Rejected' : 'Accepted', completed: true },
    { label: 'Completed', completed: false },
  ];

  return (
    <>
      <Seo title="Balloon Order Details" />
      <TrackedPageHeader
        title={intl.formatMessage({
          id: 'ballon-orders.header',
          description: 'Clinic Orders page header',
          defaultMessage: 'Balloon Order Details',
        })}
        onNavButtonClick={() => toPreviousPage()}
        button={{
          label: 'Edit Order',
          // eslint-disable-next-line no-console
          onClick: () => console.log('Edit Order'),
          trackLabel: 'edit-order',
        }}
      />
      <div className={styles.container}>
        <Card title="Order Status">
          <ProgressTracker steps={statusSteps} />
        </Card>

        {isRejected ? (
          <Card title="Rejection Reason" variant="danger-border">
            <p>
              The order has been rejected due to the following reason:{' '}
              <strong>Free of charge items were not approved</strong>
            </p>
          </Card>
        ) : (
          <Card title="Payment">
            <p>
              Payment due on <strong>2025-12-01</strong>
            </p>
            <p>
              Invoice:{' '}
              <a
                href="https://app.allurion.com/invoice.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download pdf
              </a>
            </p>
          </Card>
        )}

        <Card title="Products">
          <Table
            columns={[
              { title: 'Name', field: 'name' },
              { title: 'Ref', field: 'ref' },
              { title: 'Price', field: 'price', align: 'right' },
              { title: 'Quantity', field: 'quantity', align: 'center' },
              { title: 'Total', field: 'total', align: 'right' },
            ]}
            data={mockOrder.items}
          />
          <p style={{ margin: '1rem', textAlign: 'right', fontSize: '1.5rem', color: '#00363f' }}>
            Total: {mockOrder.total}
          </p>
          <p style={{ margin: '1rem', textAlign: 'right', fontSize: '1rem', color: '#00363f' }}>
            Shipping Cost: {mockOrder.shippingCost}
          </p>
          <p style={{ margin: '1rem', textAlign: 'right', fontSize: '1rem', color: '#00363f' }}>
            Taxes: {mockOrder.taxes}
          </p>
        </Card>

        <div className={styles.row}>
          <Card title="Shipping Information">
            <div className={styles.addressInfo}>
              <p>
                <strong>Street Address:</strong> {mockOrder.shipping.streetAddress}
              </p>
              <p>
                <strong>City:</strong> {mockOrder.shipping.city}
              </p>
              <p>
                <strong>State/Province:</strong> {mockOrder.shipping.state}
              </p>
              <p>
                <strong>Postal Code:</strong> {mockOrder.shipping.postalCode}
              </p>
              <p>
                <strong>Country:</strong> {mockOrder.shipping.country}
              </p>
              <p>
                <strong>Phone Number:</strong> {mockOrder.shipping.phoneNumber}
              </p>
              <p>
                <strong>Email:</strong> {mockOrder.shipping.email}
              </p>
              <p>
                <strong>Shipping Instructions:</strong> {mockOrder.shipping.instructions}
              </p>
              <p>
                <strong>Expected Delivery Date:</strong> {mockOrder.shipping.deliveryDate}
              </p>
            </div>
          </Card>

          <Card title="Billing Information">
            <div className={styles.addressInfo}>
              <p>
                <strong>Street Address:</strong> {mockOrder.billing.streetAddress}
              </p>
              <p>
                <strong>City:</strong> {mockOrder.billing.city}
              </p>
              <p>
                <strong>State/Province:</strong> {mockOrder.billing.state}
              </p>
              <p>
                <strong>Postal Code:</strong> {mockOrder.billing.postalCode}
              </p>
              <p>
                <strong>Country:</strong> {mockOrder.billing.country}
              </p>
              <p>
                <strong>Phone Number:</strong> {mockOrder.billing.phoneNumber}
              </p>
              <p>
                <strong>Email:</strong> {mockOrder.billing.email}
              </p>
              <p>
                <strong>VAT Identification Number:</strong> {mockOrder.billing.vatId}
              </p>
            </div>
          </Card>
        </div>

        <Card title="Additional Information">
          <p>Notes: {mockOrder.additionalInfo.notes}</p>
          <p>Internal order reference: {mockOrder.additionalInfo.clinicReferenceId}</p>
          <p className={styles.terms}>
            Terms and Conditions applicable to this Purchase Order can be reviewed:{' '}
            <a href="https://app.allurion.com/allurion-terms-conditions.html">
              https://app.allurion.com/allurion-terms-conditions.html
            </a>
          </p>
        </Card>
      </div>
    </>
  );
}

function ProgressTracker({ steps }: { steps: any[] }) {
  return (
    <div className={styles.progressTracker}>
      {steps.map((step, index) => (
        <div key={index} className={styles.progressStep}>
          <div
            className={`${styles.progressCircle} ${step.completed ? styles.completed : styles.pending}`}
          >
            {index + 1}
          </div>
          <p>{step.label}</p>
        </div>
      ))}
    </div>
  );
}
